import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Loaders from '../../../../components/loaders/Loaders';
import BreadcrumbBack from '../../../../components/breadcrumb/BreadcrumbBack';
import CustomButton from '../../../../components/button/CustomButton';
import { useNavigate } from 'react-router-dom';
import { getDynCollections, getDynCollectionsSubDir } from '../../../../service/admin/adminDynTube.service';
import { FaFolder, FaFolderOpen, FaHome, FaChevronRight, FaVideo, FaArrowLeft } from 'react-icons/fa';
import { truncateString } from '../../../../helpers/helpers';
import Pagination from '../../../../components/paginations/Pagination';

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

export default function DynIndex() {
  const navigate = useNavigate();
  const [pageLoad, setPageLoad] = React.useState(false);
  const [fetchData, setFetchData] = React.useState<any>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: 'Root' }]);
  const [hoveredFolder, setHoveredFolder] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const fetchParent = async (page: number = 1) => {
    setPageLoad(true);
    let get: any = await getDynCollections(page);
    if (get?.status === 'success') {
      setFetchData(get?.message);
      setCurrentPage(page);
    }
    setPageLoad(false);
  };

  const fetchSubDir = async (parentId: string, folderName: string, page: number = 1) => {
    setPageLoad(true);
    let get: any = await getDynCollectionsSubDir(parentId, page);
    if (get?.status === 'success') {
      setFetchData(get?.message);
      setBreadcrumbs(prev => [...prev, { id: parentId, name: folderName }]);
      setCurrentPage(page);
    }
    setPageLoad(false);
  };

  const handleBreadcrumbClick = async (index: number) => {
    const targetBreadcrumb = breadcrumbs[index];
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    setCurrentPage(1); // Reset to page 1 when navigating
    
    if (targetBreadcrumb.id === null) {
      await fetchParent(1);
    } else {
      setPageLoad(true);
      let get: any = await getDynCollectionsSubDir(targetBreadcrumb.id, 1);
      if (get?.status === 'success') {
        setFetchData(get?.message);
      }
      setPageLoad(false);
    }
  };

  const handleGoBack = async () => {
    if (breadcrumbs.length > 1) {
      const newBreadcrumbs = breadcrumbs.slice(0, -1);
      const lastItem = newBreadcrumbs[newBreadcrumbs.length - 1];
      setBreadcrumbs(newBreadcrumbs);
      setCurrentPage(1); // Reset to page 1 when going back
      
      if (lastItem.id === null) {
        await fetchParent(1);
      } else {
        setPageLoad(true);
        let get: any = await getDynCollectionsSubDir(lastItem.id, 1);
        if (get?.status === 'success') {
          setFetchData(get?.message);
        }
        setPageLoad(false);
      }
    }
  };

  React.useEffect(() => {
    fetchParent();
  }, []);

  return (
    <>
      <Helmet>
        <title>{`DynTube Videos | ${window.location.hostname}`}</title>
      </Helmet>
      {pageLoad ? (
        <Loaders type="spinner" />
      ) : (
        <div className="min-h-screen">
          <BreadcrumbBack title="Manage DynTube Videos">
            <CustomButton
              title="Manage DynTube Account"
              onClick={() => navigate('/admin/videos/dyntube/account')}
            />
          </BreadcrumbBack>

          {/* Breadcrumb Navigation */}
          <div className="my-4 bg-gradient-to-r from-purple-50 to-indigo-50 p-3 rounded-xl shadow-sm border border-purple-100">
            <div className="flex items-center gap-2 flex-wrap">
              {breadcrumbs.length > 1 && (
                <button
                  onClick={handleGoBack}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-purple-600 hover:text-purple-800 mr-2"
                >
                  <FaArrowLeft className="text-sm" />
                  <span className="text-sm font-medium">Back</span>
                </button>
              )}
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 ${
                      index === breadcrumbs.length - 1
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-purple-100 hover:text-purple-700 shadow-sm'
                    }`}
                  >
                    {index === 0 ? (
                      <FaHome className="text-sm" />
                    ) : (
                      <FaFolder className="text-sm" />
                    )}
                    <span className="text-sm font-medium">{crumb.name}</span>
                  </button>
                  {index < breadcrumbs.length - 1 && (
                    <FaChevronRight className="text-gray-400 text-xs" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Folders Grid */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FaFolderOpen className="text-yellow-500" />
                Collections
                <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {fetchData?.data?.length || 0} items
                </span>
              </h2>
            </div>

            {fetchData?.data?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {fetchData?.data?.map((item: any, index: number) => (
                  <div
                    key={index}
                    className="group relative bg-gradient-to-b from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onMouseEnter={() => setHoveredFolder(item.id)}
                    onMouseLeave={() => setHoveredFolder(null)}
                  >
                    {/* Folder Icon */}
                    <div className="flex flex-col items-center">
                      <div className="relative mb-3">
                        <FaFolder
                          className={`text-6xl transition-all duration-300 ${
                            hoveredFolder === item.id
                              ? 'text-yellow-400 transform scale-110'
                              : 'text-yellow-500'
                          }`}
                        />
                        {hoveredFolder === item.id && (
                          <div className="absolute inset-0 bg-yellow-200 rounded-full blur-xl opacity-30 -z-10" />
                        )}
                      </div>

                      {/* Folder Name */}
                      <h3
                        data-tooltip={item?.name}
                        className="text-sm font-semibold text-gray-700 text-center group-hover:text-purple-700 transition-colors duration-200"
                      >
                        {truncateString(item?.name, 15)}
                      </h3>

                      {/* Description if available */}
                      {item?.description && (
                        <p className="text-xs text-gray-400 mt-1 text-center line-clamp-1">
                          {item.description}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div
                      className={`mt-4 flex flex-col gap-2 transition-all duration-300 ${
                        hoveredFolder === item.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      <button
                        onClick={() => navigate(`/admin/videos/dyntube/${item.id}?name=${encodeURIComponent(item?.name)}`)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-xs font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                      >
                        <FaVideo />
                        Videos
                      </button>
                      <button
                        onClick={() => fetchSubDir(item.id, item.name)}
                        className="w-full flex  items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-dark text-xs font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                      >
                        <FaFolder />
                        Browse
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FaFolder className="text-8xl mb-4 opacity-30" />
                <h3 className="text-xl font-semibold text-gray-500">No Collections Found</h3>
                <p className="text-sm text-gray-400 mt-2">This folder is empty</p>
                {breadcrumbs.length > 1 && (
                  <button
                    onClick={handleGoBack}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Go Back
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {fetchData?.pagination && fetchData.pagination.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={fetchData.pagination.totalPages}
                onPageChange={(page) => {
                  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
                  if (lastBreadcrumb.id === null) {
                    fetchParent(page);
                  } else {
                    fetchSubDir(lastBreadcrumb.id, lastBreadcrumb.name, page);
                  }
                }}
              />
              <div className="mt-3 flex justify-center">
                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 text-sm text-gray-600">
                  Showing page {fetchData.pagination.page} of {fetchData.pagination.totalPages} •{' '}
                  {fetchData.pagination.totalResults} total items
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
